import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';

import { AzavistaApiService, EventType, IEvent, IEventActivity, IPagedResponse, ISearchEventActivitiesRequest, ISearchEventsRequest } from '@azavista/servicelib';
import { AzavistaSearchItemsComponent, ISearchItemsData, ISearchItemsSettingsChangedData } from '@azavista/components/search-items';
import {
    IAzavistaGroupedFieldsComponentData, IField, IIdWithLabel, IValueChangesWithObject, IAzavistaSelectedItemsCountData, IObjectEnumInputFieldValue
} from '@azavista/components/shared';
import { ISelectDialogResult } from '../event/interfaces';
import { ICreateNewEntityComponentData } from './create-new-entity/create-new-entity.interfaces';
import { CreateNewEntityComponent } from './create-new-entity/create-new-entity.component';
import { SharedService } from './shared.service';

@Injectable({
    providedIn: 'root'
})
export class CommonDialogsService {

    constructor(private matDialog: MatDialog, private sharedSvc: SharedService, private apiSvc: AzavistaApiService) {
    }

    async getTextDialog(
        key?: string,
        label?: string,
        value?: string,
        selectedItemsCountData?: IAzavistaSelectedItemsCountData
    ): Promise<ISelectDialogResult> {
        let promiseResolve: (value?: ISelectDialogResult) => void;
        const promise = new Promise<ISelectDialogResult>(resolve => {
            promiseResolve = resolve;
        });
        const groupedFields: IAzavistaGroupedFieldsComponentData = {
            editModeForAllFields: true,
            expandPanels: true,
            singleColumn: true,
            showActionButtons: true,
            selectedItemsCountData: selectedItemsCountData,
            fieldsWithEntity: {
                entity: {},
                fields: []
            }
        };
        groupedFields.fieldsWithEntity.entity[key] = value;
        groupedFields.fieldsWithEntity.fields.push(
            {
                id: key,
                name: key,
                label: label,
                category: '',
                editable: true,
                schema: { type: 'string' }
            } as IField
        );

        const actionSubject = new Subject<IValueChangesWithObject>();
        const newEntityData: ICreateNewEntityComponentData = {
            groupedFields: groupedFields,
            actionSubject: actionSubject,
            title: ''
        };
        const dialogRef = this.matDialog.open(CreateNewEntityComponent, {
            data: newEntityData,
            width: this.sharedSvc.getNewDialogCssWidth()
        });
        dialogRef.afterClosed().subscribe(() => promiseResolve());
        const actionSubscription = actionSubject.subscribe({
            next: async data => {
                if (!data || !data.obj) {
                    // Close button is clicked
                    actionSubscription.unsubscribe();
                    dialogRef.close();
                    return promiseResolve();
                }
                const obj = {
                    data: {
                        [key]: data.obj[key]
                    }
                };
                actionSubscription.unsubscribe();
                dialogRef.close();
                return promiseResolve(obj);
            }
        });
        return promise;
    }

    async showSelectEventActivitiesDialog(
        title: string, singleSelection: boolean, eventId: string, selectedItemsCountData?: IAzavistaSelectedItemsCountData
    ): Promise<IEventActivity[]> {
        const searchEventActivitiesRequest: ISearchEventActivitiesRequest = {
            ...this.sharedSvc.createDefaultPagedRequest(),
            eventId: eventId
        };
        let eventActivitiesResponse = await this.apiSvc.searchEventActivities(searchEventActivitiesRequest);
        const searchItemsDialogData: ISearchItemsData = {
            items: eventActivitiesResponse.activities,
            pagingOptions: this.sharedSvc.createPagingOptionsFromPagedResponse(eventActivitiesResponse),
            title: title,
            singleSelection: singleSelection,
            selectedItemsCountData: selectedItemsCountData,
            displayProperty: 'name'
        };
        const dialogRef = this.matDialog.open(AzavistaSearchItemsComponent, { data: searchItemsDialogData });
        const searchChangedSubscription = dialogRef.componentInstance.searchChanged.subscribe({
            next: async (searchChanges: ISearchItemsSettingsChangedData) => {
                const req: ISearchEventActivitiesRequest = {
                    eventId: eventId,
                    limit: searchChanges.paging.limit,
                    offset: searchChanges.paging.offset
                };
                req.quickSearchText = searchChanges.searchText;
                eventActivitiesResponse = await this.apiSvc.searchEventActivities(req);
                searchItemsDialogData.items = eventActivitiesResponse.activities as unknown[] as IIdWithLabel[];
                searchItemsDialogData.pagingOptions = this.sharedSvc.createPagingOptionsFromPagedResponse(eventActivitiesResponse);
            }
        });
        return new Promise(resolve => {
            dialogRef.afterClosed().subscribe({
                next: (dlgResult: IEventActivity[]) => {
                    searchChangedSubscription.unsubscribe();
                    if (dlgResult) {
                        resolve(dlgResult);
                    } else {
                        resolve(null);
                    }
                }
            });
        });
    }

    // TODO: Make this generic so we can search for any entity that supports advanced search requests and paged responses
    async showSelectEventsDialog(
        title: string, singleSelection: boolean, eventType?: EventType, selectedItemsCountData?: IAzavistaSelectedItemsCountData
    ): Promise<IEvent[]> {
        const searchEventsRequest: ISearchEventsRequest = {
            ...this.sharedSvc.createDefaultPagedRequest(),
        };
        if (eventType) {
            searchEventsRequest.type = eventType;
        }
        let eventsResponse = await this.apiSvc.searchEvents(searchEventsRequest);
        const searchItemsDialogData: ISearchItemsData = {
            items: eventsResponse.events,
            pagingOptions: this.sharedSvc.createPagingOptionsFromPagedResponse(eventsResponse),
            title: title,
            singleSelection: singleSelection,
            selectedItemsCountData: selectedItemsCountData,
            displayProperty: 'name'
        };
        const dialogRef = this.matDialog.open(AzavistaSearchItemsComponent, { data: searchItemsDialogData });
        const searchChangedSubscription = dialogRef.componentInstance.searchChanged.subscribe({
            next: async (searchChanges: ISearchItemsSettingsChangedData) => {
                const req: ISearchEventsRequest = {
                    limit: searchChanges.paging.limit,
                    offset: searchChanges.paging.offset
                };
                if (eventType) {
                    req.type = eventType;
                }
                req.quickSearchText = searchChanges.searchText;
                eventsResponse = await this.apiSvc.searchEvents(req);
                searchItemsDialogData.items = eventsResponse.events;
                searchItemsDialogData.pagingOptions = this.sharedSvc.createPagingOptionsFromPagedResponse(eventsResponse);
            }
        });
        return new Promise(resolve => {
            dialogRef.afterClosed().subscribe({
                next: (dlgResult: IEvent[]) => {
                    searchChangedSubscription.unsubscribe();
                    if (dlgResult) {
                        resolve(dlgResult);
                    } else {
                        resolve(null);
                    }
                }
            });
        });
    }

    // async showSelectEventsDialog(
    //     title: string, singleSelection: boolean, eventType?: EventType, selectedItemsCountData?: IAzavistaSelectedItemsCountData
    // ): Promise<IEvent[]> {
    //     const searchEventsRequest: ISearchEventsRequest = {
    //         ...this.sharedSvc.createDefaultPagedRequest(),
    //     };
    //     if (eventType) {
    //         searchEventsRequest.type = eventType;
    //     }
    //     let eventsResponse = await this.apiSvc.searchEvents(searchEventsRequest);
    //     const searchItemsDialogData: ISearchItemsData = {
    //         items: eventsResponse.events,
    //         pagingOptions: this.sharedSvc.createPagingOptionsFromPagedResponse(eventsResponse),
    //         title: title,
    //         singleSelection: singleSelection,
    //         selectedItemsCountData: selectedItemsCountData,
    //         displayProperty: 'name'
    //     };
    //     const dialogRef = this.matDialog.open(AzavistaSearchItemsComponent, { data: searchItemsDialogData });
    //     const searchChangedSubscription = dialogRef.componentInstance.searchChanged.subscribe({
    //         next: async (searchChanges: ISearchItemsSettingsChangedData) => {
    //             const req: ISearchEventsRequest = {
    //                 limit: searchChanges.paging.limit,
    //                 offset: searchChanges.paging.offset
    //             };
    //             if (eventType) {
    //                 req.type = eventType;
    //             }
    //             req.quickSearchText = searchChanges.searchText;
    //             eventsResponse = await this.apiSvc.searchEvents(req);
    //             searchItemsDialogData.items = eventsResponse.events;
    //             searchItemsDialogData.pagingOptions = this.sharedSvc.createPagingOptionsFromPagedResponse(eventsResponse);
    //         }
    //     });
    //     return new Promise(resolve => {
    //         dialogRef.afterClosed().subscribe({
    //             next: (dlgResult: IEvent[]) => {
    //                 searchChangedSubscription.unsubscribe();
    //                 if (dlgResult) {
    //                     resolve(dlgResult);
    //                 } else {
    //                     resolve(null);
    //                 }
    //             }
    //         });
    //     });
    // }

    async showMultiSelectDialog<TResponse extends IPagedResponse, TItem>(
        title: string,
        singleSelection: boolean,
        searchFn: (searchChanges: ISearchItemsSettingsChangedData) => Promise<TResponse>,
        responseItemsSelectorFn: (res: TResponse) => TItem[],
        selectedItemsCountData?: IAzavistaSelectedItemsCountData,
    ): Promise<TItem[]> {
        const res = await searchFn({ paging: { ...this.sharedSvc.createDefaultPagedRequest() } } as ISearchItemsSettingsChangedData);
        let searchItemsDialogData: ISearchItemsData = {
            items: responseItemsSelectorFn(res),
            pagingOptions: this.sharedSvc.createPagingOptionsFromPagedResponse(res),
            title: title,
            singleSelection: singleSelection,
            selectedItemsCountData: selectedItemsCountData,
            displayProperty: 'name',
        };
        const dialogRef = this.matDialog.open(AzavistaSearchItemsComponent, { data: searchItemsDialogData });
        const searchChangedSubscription = dialogRef.componentInstance.searchChanged.subscribe({
            next: async (searchChanges: ISearchItemsSettingsChangedData) => {
                const res = await searchFn(searchChanges);
                searchItemsDialogData.items = responseItemsSelectorFn(res);
                searchItemsDialogData.pagingOptions = this.sharedSvc.createPagingOptionsFromPagedResponse(res);
            }
        });
        return new Promise(resolve => {
            dialogRef.afterClosed().subscribe({
                next: (dlgResult: TItem[]) => {
                    searchChangedSubscription.unsubscribe();
                    if (dlgResult) {
                        resolve(dlgResult);
                    } else {
                        resolve(null);
                    }
                }
            });
        });
    }

    async getSelectDialog(
        key?: string,
        label?: string,
        allItems?: any[],
        selectedId?: string,
        selectedItemsCountData?: IAzavistaSelectedItemsCountData
    ): Promise<ISelectDialogResult> {
        let promiseResolve: (value?: ISelectDialogResult) => void;
        const promise = new Promise<ISelectDialogResult>(resolve => {
            promiseResolve = resolve;
        });
        const groupedFields: IAzavistaGroupedFieldsComponentData = {
            editModeForAllFields: true,
            expandPanels: true,
            singleColumn: true,
            showActionButtons: true,
            selectedItemsCountData: selectedItemsCountData,
            fieldsWithEntity: {
                entity: {},
                fields: []
            }
        };
        const val: IObjectEnumInputFieldValue = {
            items: allItems,
            selected: allItems.find(x => x.id === selectedId),
            displayProperty: 'name'
        };
        groupedFields.fieldsWithEntity.entity[key] = val;
        groupedFields.fieldsWithEntity.fields.push(
            {
                id: key,
                name: key,
                label: label,
                category: '',
                editable: true,
                component: 'object_enum',
                schema: { type: 'object' }
            } as IField
        );

        const actionSubject = new Subject<IValueChangesWithObject>();
        const newEntityData: ICreateNewEntityComponentData = {
            groupedFields: groupedFields,
            actionSubject: actionSubject,
            title: ''
        };
        const newTeamDialogRef = this.matDialog.open(CreateNewEntityComponent, {
            data: newEntityData,
            width: this.sharedSvc.getNewDialogCssWidth()
        });
        const actionSubscription = actionSubject.subscribe({
            next: async data => {
                if (!data || !data.obj) {
                    // Close button is clicked
                    actionSubscription.unsubscribe();
                    newTeamDialogRef.close();
                    return promiseResolve();
                }
                const obj = {
                    data: {
                        ...data.obj[key]
                    }
                };
                actionSubscription.unsubscribe();
                newTeamDialogRef.close();
                return promiseResolve(obj);
            }
        });
        return promise;
    }
}
